import React, { useState, useEffect } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  TextField,
  Select,
  Popover,
  Checkbox,
  Button,
} from '@shopify/polaris';

const CreateStoryModal = ({ 
  isOpen, 
  onClose, 
  setToastMessage, 
  setToastActive,
  onStoryCreated 
}) => {
  const [isCreatingStory, setIsCreatingStory] = useState(false);
  const [agePopoverActive, setAgePopoverActive] = useState(false);
  
  const ageOptions = ['0-2', '2-4', '4-6', '6-8', '8-10'];

  const [newStoryData, setNewStoryData] = useState({
    slug: '',
    title: '',
    description: '',
    gender: 'boy',
    age: [],
    language: 'english'
  });

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  // Add this useEffect to force Age dropdown popover width to 100%
  useEffect(() => {
    if (agePopoverActive) {
      setTimeout(() => {
        const popover = document.querySelector('.Polaris-Popover[data-age-popover="create-story"] .Polaris-Popover__ContentContainer');
        if (popover) {
          popover.style.width = '100%';
          popover.style.minWidth = '100%';
          popover.style.maxWidth = '100%';
        }
      }, 0);
    }
  }, [agePopoverActive]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setNewStoryData({
        slug: '',
        title: '',
        description: '',
        gender: 'boy',
        age: [],
        language: 'english'
      });
      setAgePopoverActive(false);
    }
  }, [isOpen]);

  const handleCreateStory = async () => {
    if (!newStoryData.title || !newStoryData.slug) {
      setToastMessage('Please fill in all required fields');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);
      return;
    }

    setIsCreatingStory(true);
    
    try {
      const storyData = {
        ...newStoryData,
        age: newStoryData.age.join(', ')
      };
      
      // Import the service here to avoid circular dependencies
      const { storiesService } = await import('../../services/storiesService');
      
      const result = await storiesService.createStory(storyData);
      
      setToastMessage('Story Created Successfully');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);
      
      // Reset form
      setNewStoryData({
        slug: '',
        title: '',
        description: '',
        gender: 'boy',
        age: [],
        language: 'english'
      });
      
      onClose();
      
      // Notify parent component to refresh stories list
      if (onStoryCreated) {
        onStoryCreated();
      }
    } catch (err) {
      console.error('Error creating story:', err);
      const errorMessage = err.message || 'Failed to create story';
      setToastMessage(`Error: ${errorMessage}`);
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 5000);
    } finally {
      setIsCreatingStory(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{padding: '16px'}}>{'Create New Story'}</div>}
      primaryAction={{
        content: 'Create',
        onAction: handleCreateStory,
        loading: isCreatingStory,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
      footer={<div style={{padding: '16px'}}></div>}
    >
      <BlockStack gap="400">
        <TextField
          label="Title"
          value={newStoryData.title}
          onChange={(value) => setNewStoryData(prev => ({ 
            ...prev, 
            title: value,
            slug: generateSlug(value) // Auto-generate slug from title
          }))}
          placeholder="Story Title"
        />
        <TextField
          label="Slug"
          value={newStoryData.slug}
          onChange={(value) => setNewStoryData(prev => ({ ...prev, slug: value }))}
          placeholder="story-slug"
          readOnly
        />
        <TextField
          label="Description"
          value={newStoryData.description}
          onChange={(value) => setNewStoryData(prev => ({ ...prev, description: value }))}
          multiline={2}
          autoComplete="off"
          placeholder="Story description..."
        />
        <Select
          label="Gender"
          options={[
            { label: 'Boy', value: 'boy' },
            { label: 'Girl', value: 'girl' },
            { label: 'Both', value: 'both' },
          ]}
          onChange={(value) => setNewStoryData(prev => ({ ...prev, gender: value }))}
          value={newStoryData.gender}
        />
        {/* Age Selection */}
        <div>
          <Text variant="bodyMd" as="label" style={{ display: 'block', marginBottom: 4, textAlign: 'left' }}>Age</Text>
          <Popover
            active={agePopoverActive}
            activator={
              <Button disclosure fullWidth onClick={() => setAgePopoverActive((a) => !a)} style={{ 
                textAlign: 'left', 
                justifyContent: 'flex-start',
                paddingLeft: '12px',
                paddingRight: '12px'
              }}>
                <div style={{ 
                  width: '100%', 
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'flex-start'
                }}>
                  {newStoryData.age.length > 0 ? newStoryData.age.join(', ') : 'Select Age'}
                </div>
              </Button>
            }
            onClose={() => setAgePopoverActive(false)}
            fullWidth
            data-age-popover="create-story"
          >
            <BlockStack gap="200" style={{ padding: 12, minWidth: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
              {ageOptions.map(range => (
                <Checkbox
                  key={range}
                  label={range}
                  checked={newStoryData.age.includes(range)}
                  onChange={(checked) => {
                    setNewStoryData(prev => ({
                      ...prev,
                      age: checked
                        ? [...prev.age, range]
                        : prev.age.filter(a => a !== range)
                    }));
                  }}
                />
              ))}
            </BlockStack>
          </Popover>
        </div>
        <Select
          label="Language"
          options={[
            { label: 'Arabic (Egyptian)', value: 'arabic-egyptian' },
            { label: 'Arabic (Saudi Arabia)', value: 'arabic-saudi' },
            { label: 'Arabic (UAE)', value: 'arabic-uae' },
            { label: 'Arabic (Qatar)', value: 'arabic-qatar' },
            { label: 'Arabic (Kuwait)', value: 'arabic-kuwait' },
            { label: 'English', value: 'english' },
            { label: 'Spanish', value: 'spanish' },
            { label: 'German', value: 'german' },
            { label: 'French', value: 'french' },
            { label: 'Italian', value: 'italian' },
          ]}
          onChange={(value) => setNewStoryData(prev => ({ ...prev, language: value }))}
          value={newStoryData.language}
        />
      </BlockStack>
    </Modal>
  );
};

export default CreateStoryModal; 